import { useMutation, useQuery } from '@tanstack/react-query';

import type { BoardOperation } from '~/pages/LeLens/document/editor';
import type { Condition } from '~/pages/LeLens/document/types';

import { reportingLeLensPath } from '~/consts/reportingLeLensApi';

import { request } from '~/services/common';

import { resolveReportingLeLensUrl } from '~/utils/resolveReportingLeLensUrl';

import { encodedJsonBody } from './encodedJsonBody';
import { reportingLeLensQueryKeys } from './useGetReportingLeLens';

export type LensFieldColumn = { name: string; label: string; kind: 'date' | 'dimension' | 'measure'; type: string };

export type LensFieldDataset = {
  id: string;
  kind: 'catalogue' | 'sql' | 'table';
  label: string;
  catalogue?: string;
  sql?: string;
  columns: Array<LensFieldColumn>;
  certified: Array<{ id: string; label: string; format?: string; definition?: string }>;
  usedBy?: Array<string>;
  date?: string | null;
  dateGrain?: string;
  ready?: string;
  labels?: Record<string, string>;
  derived?: Record<string, string>;
  problem?: string;
};

export type LensFieldMeasure = {
  id: string;
  label: string;
  format?: string;
  dataset?: string;
  certified: boolean;
  expr?: string;
  column?: string;
  definition?: string;
  lowerIsBetter?: boolean;
  decimals?: number;
  where?: Record<string, Condition>;
  minN?: [string, number];
  internal?: boolean;
  rollup?: 'sum' | 'none';
  problem?: string;
};

export type LensFields = {
  lensId: string;
  lensVersion: number;
  datasets: Array<LensFieldDataset>;
  measures: Array<LensFieldMeasure>;
  reach: Record<string, Array<string>>;
};

export type LensCheckProblem = {
  code: string;
  path: string;
  message: string;
  remedy?: string;
  severity?: 'error' | 'warning';
};

export type LensCheck = {
  refused: boolean;
  problems: Array<LensCheckProblem>;
  preview?: { columns: Array<string>; rows: Array<Record<string, unknown>>; error?: string };
};

export function useGetReportingLensFields(lensId: string, lensVersion: number | undefined, enabled = true) {
  return useQuery({
    queryKey: [...reportingLeLensQueryKeys.detail(lensId), 'fields', lensVersion],
    enabled: enabled && lensVersion !== undefined,
    queryFn: async ({ signal }) =>
      (await request(resolveReportingLeLensUrl(`${reportingLeLensPath(lensId)}/fields`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ lensVersion }),
        signal,
      })) as LensFields,
    placeholderData: (previous) => (previous?.lensId === lensId ? previous : undefined),
    retry: false,
    staleTime: 60_000,
  });
}

export function useCheckLensOperations(lensId: string) {
  return useMutation({
    mutationFn: async (body: { operations: Array<BoardOperation>; preview?: string }) =>
      (await request(resolveReportingLeLensUrl(`${reportingLeLensPath(lensId)}/check`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: encodedJsonBody(body),
      })) as LensCheck,
  });
}
